import { NetworkStatus } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { AUDIT_SOURCE_SYSTEMS, AUDITS } from '@optools/services/operator-gateway/audit/queries';
import { AuditTable } from '@optools/tools/audit/components/audits/audit-table';
import { AuditTableFilters } from '@optools/tools/audit/components/audits/audit-table-filters';
import { AuditTableSkeleton } from '@optools/tools/audit/components/audits/audit-table-skeleton';
import { AuditFilters, auditSearchParamsSchema } from '@optools/tools/audit/schemas/audits';
import { createFileRoute, stripSearchParams, useNavigate, useSearch } from '@tanstack/react-router';
import { PaginationState } from '@tanstack/react-table';
import { zodValidator } from '@tanstack/zod-adapter';
import { omit } from 'es-toolkit';

/* eslint-disable perfectionist/sort-objects */
export const Route = createFileRoute('/_auth/audit/audits')({
  component: RouteComponent,
  validateSearch: zodValidator(auditSearchParamsSchema),
  search: {
    middlewares: [
      stripSearchParams({
        auditId: undefined,
        dialog: undefined,
        email: '',
        instigator: '',
        limit: 25,
        operations: [],
        page: 0,
        sourceSystems: [],
        tag: undefined,
        traceId: '',
      }),
    ],
  },
});
/* eslint-enable perfectionist/sort-objects */

export function RouteComponent() {
  const params = useSearch({ from: '/_auth/audit/audits' });
  const navigate = useNavigate();

  const variables = omit(params, ['auditId', 'dialog']);
  const { limit, page, ...filters } = variables;

  const auditSourceSystemsResult = useQuery(AUDIT_SOURCE_SYSTEMS);
  const auditsQueryResult = useQuery(AUDITS, { fetchPolicy: 'network-only', variables });

  const handleUpdateFilter = <TKey extends keyof AuditFilters>(
    key: TKey,
    value: AuditFilters[TKey],
  ) => {
    navigate({
      search: (currentParams) => ({ ...currentParams, [key]: value, page: 0 }),
      to: '.',
    });
  };

  const handleResetFilters = () => {
    navigate({
      search: (currentParams) => ({ endDate: null, limit: currentParams.limit, startDate: null }),
      to: '.',
    });
  };

  const handleUpdatePagination = ({ pageIndex, pageSize }: PaginationState) => {
    navigate({
      search: (currentParams) => ({ ...currentParams, limit: pageSize, page: pageIndex }),
      to: '.',
    });
  };

  const { data, previousData } = auditsQueryResult;

  const audits = data?.audits.audits ?? previousData?.audits.audits ?? [];
  const total = data?.audits.total ?? previousData?.audits.total;

  // Show skeleton on initial load only
  if (
    auditSourceSystemsResult.networkStatus === NetworkStatus.loading ||
    auditsQueryResult.networkStatus === NetworkStatus.loading
  ) {
    return <AuditTableSkeleton />;
  }

  return (
    <AuditTable
      audits={audits}
      pageIndex={page}
      pageSize={limit}
      total={total}
      onPaginationChange={handleUpdatePagination}>
      <AuditTableFilters
        auditSourceSystems={auditSourceSystemsResult.data?.auditSourceSystems ?? []}
        filters={filters}
        loading={auditsQueryResult.loading}
        onResetFilters={handleResetFilters}
        onUpdateFilter={handleUpdateFilter}
      />
    </AuditTable>
  );
}
